import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { AlterarTagsOrdemProducaoRequest } from '../../requests/suprimentos/alterar-tags-ordem-producao.request';
import { AjustarTemposEtapaOrdemProducaoRequest } from '../../requests/suprimentos/ajustar-tempos-etapa.request';
import { AlterarStatusOrdemProducaoRequest } from '../../requests/suprimentos/alterar-status-ordem-producao.request';
import { OrdemProducaoLookupResponse } from '../../responses/suprimentos/ordem-producao-lookup.response';
import { OrdemProducaoExclusaoResponse } from '../../responses/suprimentos/ordem-producao-exclusao.response';

/**
 * Chamadas que são puro domínio da ordem de produção e não precisam agregar outra API: excluir,
 * alterar tags em lote, lookup de catálogos e a execução das etapas (iniciar/concluir/ajustar
 * tempos) e a alteração manual de status. Vão direto na API.Suprimentos.
 *
 * O que precisa juntar produto e usuário — lista, composição, salvar, hierarquia, histórico — está
 * no OrdemProducaoOrquestradorService.
 */
@Injectable({ providedIn: 'root' })
export class OrdemProducaoService extends CommonService {
  private readonly apiUrl: string = environment.urlApiSuprimentos + 'OrdemProducao';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /** Status da OP, status de etapa e tags de produção da empresa. */
  public getLookup(): Observable<OrdemProducaoLookupResponse> {
    return this.httpClient
      .get<OrdemProducaoLookupResponse>(`${this.apiUrl}/lookup`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Exclusão lógica: o histórico da OP é preservado. Com estoque Pendente ou Lançado o backend não
   * exclui e o caller é obrigado a excluir o estoque antes (seção 3.8 da US), sem opção de pular essa etapa.
   */
  public excluir(idOrdemProducao: number): Observable<OrdemProducaoExclusaoResponse> {
    return this.httpClient
      .delete<OrdemProducaoExclusaoResponse>(`${this.apiUrl}/${idOrdemProducao}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public alterarTagsEmLote(request: AlterarTagsOrdemProducaoRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/tags/bulk`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Bloqueio sequencial e transições são validados no backend (seção 8.5 da US). */
  public iniciarEtapa(idOrdemProducao: number, idOrdemProducaoEtapa: number): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/${idOrdemProducao}/etapa/${idOrdemProducaoEtapa}/iniciar`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public concluirEtapa(idOrdemProducao: number, idOrdemProducaoEtapa: number): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/${idOrdemProducao}/etapa/${idOrdemProducaoEtapa}/concluir`, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public ajustarTemposEtapa(
    idOrdemProducao: number,
    idOrdemProducaoEtapa: number,
    request: AjustarTemposEtapaOrdemProducaoRequest,
  ): Observable<ReturnStruct> {
    return this.httpClient
      .put<ReturnStruct>(`${this.apiUrl}/${idOrdemProducao}/etapa/${idOrdemProducaoEtapa}/tempos`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  public alterarStatus(idOrdemProducao: number, request: AlterarStatusOrdemProducaoRequest): Observable<ReturnStruct> {
    return this.httpClient
      .post<ReturnStruct>(`${this.apiUrl}/${idOrdemProducao}/status`, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
